/**
 * Facility Mapper Utility
 * Maps between form values and API request/response formats
 */

import type { Facility, FacilityCreateRequest, FacilityUser } from '../types/facility.types';
import type { User } from '../types/user.types';
import { DEFAULT_PACS_IP_ADDRESS } from '../constants/facilityDefaults';

/** Facility code: uppercase A–Z / 0–9 only, max length (matches form + Yup). */
export const FACILITY_CODE_MAX_LENGTH = 20;

export function normalizeFacilityCodeInput(value: unknown): string {
  return String(value ?? '')
    .toUpperCase()
    .replaceAll(/[^A-Z0-9]/g, '')
    .slice(0, FACILITY_CODE_MAX_LENGTH);
}

/** First non-empty string from object keys (handles snake_case + camelCase API shapes). */
function coerceRecordString(value: unknown): string {
  if (value == null) {
    return '';
  }
  if (typeof value === 'string') {
    return value.trim();
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value).trim();
  }
  return '';
}

export function pickFromRecord(
  obj: Record<string, unknown> | null | undefined,
  ...keys: string[]
): string {
  if (!obj) {
    return '';
  }
  for (const k of keys) {
    const normalized = coerceRecordString(obj[k]);
    if (normalized !== '') {
      return normalized;
    }
  }
  return '';
}

const FACILITY_TYPES_FOR_FORM = [
  'Diagnostic Center',
  'Hospital',
  'Clinic',
  'HUB',
  'SPOKE',
] as const;

function normalizeFacilityTypeForForm(value: string): string {
  if (!value) {
    return '';
  }
  const t = value.trim();
  const upper = t.toUpperCase();
  if (upper === 'HUB') {
    return 'HUB';
  }
  if (upper === 'SPOKE') {
    return 'SPOKE';
  }
  const hit = FACILITY_TYPES_FOR_FORM.find(a => a.toLowerCase() === t.toLowerCase());
  return hit || t;
}

/** API expects HUB / SPOKE; legacy types are sent as-is (e.g. Diagnostic Center). */
export function mapFacilityTypeToApi(type: string): string {
  const t = type.trim();
  const upper = t.toUpperCase();
  if (upper === 'HUB') {
    return 'HUB';
  }
  if (upper === 'SPOKE') {
    return 'SPOKE';
  }
  return t;
}

function normalizeStatusForForm(value: string): string {
  if (!value) {
    return 'Active';
  }
  const allowed = ['Active', 'Inactive', 'Suspended'] as const;
  const t = value.trim();
  const hit = allowed.find(a => a.toLowerCase() === t.toLowerCase());
  return hit || t;
}

/** Form gender buttons use Male / Female / Other. */
function normalizeGenderForForm(value: string | undefined | null): string {
  if (value == null || String(value).trim() === '') {
    return '';
  }
  const lower = String(value).trim().toLowerCase();
  if (lower === 'male' || lower === 'm') {
    return 'Male';
  }
  if (lower === 'female' || lower === 'f') {
    return 'Female';
  }
  if (lower === 'other' || lower === 'others' || lower === 'o') {
    return 'Other';
  }
  if (value === 'Male' || value === 'Female' || value === 'Other') {
    return value;
  }
  return '';
}

/**
 * Extract filename from file path
 * @param path - File path (e.g., "/uploads/logos/file.png")
 * @returns Filename (e.g., "file.png") or empty string if path is invalid
 */
export function extractFilenameFromPath(path: string | null | undefined): string {
  if (!path) {
    return '';
  }
  // Extract filename from path (handle both forward and backward slashes)
  const parts = path.split(/[/\\]/);
  return parts.at(-1) || '';
}

/**
 * Form values structure (from AddFacility component)
 */
export interface FacilityFormValues {
  // Facility Details
  name: string;
  code: string;
  type: string;
  description: string;
  phoneNo: string;
  emailId: string;

  // Facility Address
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  pincode: string;
  country: string;

  // Letterhead Configuration
  logo: File | null;
  logoFileName?: string; // Filename from API response for display
  headerText: string;
  footerText: string;

  // PACS/RIS Integration
  pacsAETitle: string;
  pacsIPAddress: string;
  pacsPort: string;
  integrationStatus: string;
  status: string;

  // Facility Admin Details
  employeeId: string;
  fullName: string;
  adminPhoneNo: string;
  adminEmailId: string;
  gender: string;
  username: string;
  password: string;
  designation?: string;
  hasRadiologistHierarchy?: string;

  /** Optional — appended on multipart update when set */
  risUrl?: string;
  /** When no new logo file, send existing or edited logo URL/path */
  letterheadLogoUrl?: string;
  seniorRadiologistReviewRequired?: string;
  /** Hub facility IDs when type is SPOKE (string IDs for form controls). */
  linkedHubFacilityIds?: string[];
}

/**
 * Map form values to API create request format
 */
export function mapFormValuesToCreateRequest(
  formValues: FacilityFormValues
): FacilityCreateRequest {
  return {
    facility_name: formValues.name,
    facility_code: normalizeFacilityCodeInput(formValues.code?.trim() || ''),
    facility_type: mapFacilityTypeToApi(formValues.type),
    facility_description: formValues.description || undefined,
    address_line_1: formValues.addressLine1,
    address_line_2: formValues.addressLine2,
    city: formValues.city,
    state: formValues.state,
    country: formValues.country,
    pincode: formValues.pincode,
    contact_number: formValues.phoneNo,
    email_id: formValues.emailId,
    letterhead_logo: formValues.logo ? formValues.logo.name : undefined,
    header_text: formValues.headerText || undefined,
    footer_text: formValues.footerText || undefined,
    pacs_ae_title: formValues.pacsAETitle || undefined,
    pacs_ip_address: formValues.pacsIPAddress || undefined,
    pacs_port: formValues.pacsPort ? parseInt(formValues.pacsPort, 10) : undefined,
    ris_url: undefined, // Not in form, can be added later if needed
    integration_status: formValues.integrationStatus || 'Pending', // Default to 'Pending'
    status: formValues.status || 'Active', // Default to 'Active'
    // Facility Admin Details
    employee_id: formValues.employeeId || undefined,
    full_name: formValues.fullName || undefined,
    admin_phone_no: formValues.adminPhoneNo
      ? formValues.adminPhoneNo.replaceAll(/\s+/g, '')
      : undefined,
    admin_email_id: formValues.adminEmailId || undefined,
    gender: formValues.gender || undefined,
    username: formValues.username || undefined,
    password: formValues.password || undefined,
    designation: formValues.designation || undefined,
    has_radiologist_hierarchy: formValues.hasRadiologistHierarchy === 'Yes',
    ...(formValues.hasRadiologistHierarchy === 'Yes' &&
    formValues.seniorRadiologistReviewRequired?.trim()
      ? { senior_radiologist_review_required: formValues.seniorRadiologistReviewRequired.trim() }
      : {}),
  };
}

/**
 * Convert facility form values to FormData for multipart/form-data upload
 */
function appendIfPresent(formData: FormData, key: string, value: string | undefined | null): void {
  if (value) {
    formData.append(key, value);
  }
}

function appendLinkedHubIds(formData: FormData, formValues: FacilityFormValues): void {
  if (
    mapFacilityTypeToApi(formValues.type) !== 'SPOKE' ||
    !formValues.linkedHubFacilityIds?.length
  ) {
    return;
  }
  for (const hubId of formValues.linkedHubFacilityIds) {
    if (hubId) {
      formData.append('linked_hub_facility_ids', hubId);
    }
  }
}

function appendLetterheadLogo(formData: FormData, formValues: FacilityFormValues): void {
  if (formValues.logo) {
    formData.append('letterhead_logo', formValues.logo);
  } else if (formValues.letterheadLogoUrl?.trim()) {
    formData.append('letterhead_logo', formValues.letterheadLogoUrl.trim());
  }
}

function appendAdminGender(formData: FormData, gender: string | undefined): void {
  if (!gender) {
    return;
  }
  const genderMap: Record<string, string> = {
    male: 'Male',
    female: 'Female',
    others: 'Other',
    other: 'Other',
  };
  const normalizedGender = genderMap[gender.toLowerCase()] || gender;
  formData.append('gender', normalizedGender);
}

function appendAdminPassword(formData: FormData, password: string | undefined): void {
  const PASSWORD_PLACEHOLDER = '••••••';
  if (password?.trim() && password !== PASSWORD_PLACEHOLDER) {
    formData.append('password', password);
  }
}

function appendFacilityCoreFields(formData: FormData, formValues: FacilityFormValues): void {
  formData.append('facility_name', formValues.name);
  const facilityCode = normalizeFacilityCodeInput(formValues.code || '');
  appendIfPresent(formData, 'facility_code', facilityCode || null);
  formData.append('facility_type', mapFacilityTypeToApi(formValues.type));
  appendLinkedHubIds(formData, formValues);
  appendIfPresent(formData, 'facility_description', formValues.description);
  formData.append('address_line_1', formValues.addressLine1);
  appendIfPresent(formData, 'address_line_2', formValues.addressLine2);
  formData.append('city', formValues.city);
  formData.append('state', formValues.state);
  formData.append('country', formValues.country);
  formData.append('pincode', formValues.pincode);
  formData.append('contact_number', formValues.phoneNo);
  formData.append('email_id', formValues.emailId);
  appendLetterheadLogo(formData, formValues);
  appendIfPresent(formData, 'header_text', formValues.headerText);
  appendIfPresent(formData, 'footer_text', formValues.footerText);
  appendIfPresent(formData, 'pacs_ae_title', formValues.pacsAETitle);
  appendIfPresent(formData, 'pacs_ip_address', formValues.pacsIPAddress);
  appendIfPresent(formData, 'pacs_port', formValues.pacsPort);
  formData.append('integration_status', formValues.integrationStatus || 'Pending');
  formData.append('status', formValues.status || 'Active');
}

function appendFacilityAdminFields(formData: FormData, formValues: FacilityFormValues): void {
  appendIfPresent(formData, 'employee_id', formValues.employeeId);
  appendIfPresent(formData, 'full_name', formValues.fullName);
  if (formValues.adminPhoneNo) {
    formData.append('admin_phone_no', formValues.adminPhoneNo.replaceAll(/\s+/g, ''));
  }
  appendIfPresent(formData, 'admin_email_id', formValues.adminEmailId);
  appendAdminGender(formData, formValues.gender);
  appendIfPresent(formData, 'username', formValues.username);
  appendAdminPassword(formData, formValues.password);
  appendIfPresent(formData, 'designation', formValues.designation);
  formData.append(
    'has_radiologist_hierarchy',
    formValues.hasRadiologistHierarchy === 'Yes' ? 'true' : 'false'
  );
}

export function mapFormValuesToFormData(formValues: FacilityFormValues): FormData {
  const formData = new FormData();
  appendFacilityCoreFields(formData, formValues);
  appendFacilityAdminFields(formData, formValues);

  if (formValues.risUrl?.trim()) {
    formData.append('ris_url', formValues.risUrl.trim());
  }
  if (
    formValues.hasRadiologistHierarchy === 'Yes' &&
    formValues.seniorRadiologistReviewRequired?.trim()
  ) {
    formData.append(
      'senior_radiologist_review_required',
      formValues.seniorRadiologistReviewRequired.trim()
    );
  }

  return formData;
}

function valueToDisplayString(value: unknown): string {
  if (value == null) {
    return '';
  }
  if (typeof value === 'string') {
    return value.trim();
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return '';
}

function firstNonEmpty(...values: unknown[]): string {
  for (const value of values) {
    const normalized = valueToDisplayString(value);
    if (normalized !== '') {
      return normalized;
    }
  }
  return '';
}

function pickFacilityField(
  root: Record<string, unknown>,
  facilityValue: unknown,
  ...keys: string[]
): string {
  const fromRoot = pickFromRecord(root, ...keys);
  if (fromRoot) {
    return fromRoot;
  }
  return valueToDisplayString(facilityValue);
}

function pickFacilityStringOrDefault(
  root: Record<string, unknown>,
  facilityValue: unknown,
  keys: string[],
  defaultValue: string
): string {
  const fromRoot = pickFromRecord(root, ...keys);
  if (fromRoot) {
    return fromRoot;
  }
  const fromFacility = valueToDisplayString(facilityValue);
  if (fromFacility) {
    return fromFacility;
  }
  return defaultValue;
}

/**
 * Map API response to form values format
 * @param facility - Facility object from API
 * @param adminUser - Optional admin user object with full details
 */
export function mapApiResponseToFormValues(
  facility: Facility,
  adminUser?: User | null
): Partial<FacilityFormValues> {
  const root = facility as unknown as Record<string, unknown>;
  const usersRaw =
    facility.users ??
    (root.users as FacilityUser | null | undefined) ??
    (root.Users as FacilityUser | null | undefined) ??
    (root.admin_user as FacilityUser | null | undefined) ??
    (root.adminUser as FacilityUser | null | undefined);
  const userRec = usersRaw as unknown as Record<string, unknown> | null | undefined;

  const fromUser = (...keys: string[]) => pickFromRecord(userRec ?? null, ...keys);
  const pick = (...keys: string[]) => pickFromRecord(root, ...keys);

  const name = pickFacilityField(root, facility.facility_name, 'facility_name', 'name');
  const code = normalizeFacilityCodeInput(
    pickFacilityField(root, facility.facility_code, 'facility_code', 'code')
  );
  const typeRaw = pickFacilityField(root, facility.facility_type, 'facility_type', 'type');
  const description = pickFacilityField(
    root,
    facility.facility_description,
    'facility_description',
    'description'
  );
  const phoneNo = pickFacilityField(
    root,
    facility.contact_number,
    'contact_number',
    'contactNumber',
    'phoneNo',
    'phone_no'
  );
  const emailId = pickFacilityField(root, facility.email_id, 'email_id', 'emailId', 'email');
  const addressLine1 = pickFacilityField(
    root,
    facility.address_line_1,
    'address_line_1',
    'addressLine1'
  );
  const addressLine2 = pickFacilityField(
    root,
    facility.address_line_2,
    'address_line_2',
    'addressLine2'
  );
  const city = pickFacilityField(root, facility.city, 'city');
  const state = pickFacilityField(root, facility.state, 'state');
  const pincode = pickFacilityField(root, facility.pincode, 'pincode', 'pin_code');
  const country = pickFacilityField(root, facility.country, 'country');

  const letterheadPath = pickFacilityField(
    root,
    facility.letterhead_logo,
    'letterhead_logo',
    'letterheadLogo'
  );
  const headerText = pickFacilityField(root, facility.header_text, 'header_text', 'headerText');
  const footerText = pickFacilityField(root, facility.footer_text, 'footer_text', 'footerText');

  const pacsAETitle = pickFacilityStringOrDefault(
    root,
    facility.pacs_ae_title,
    ['pacs_ae_title', 'pacsAETitle'],
    'DCM4CHEE'
  );
  const pacsIPAddress = pickFacilityStringOrDefault(
    root,
    facility.pacs_ip_address,
    ['pacs_ip_address', 'pacsIPAddress'],
    DEFAULT_PACS_IP_ADDRESS
  );
  const pacsPortRaw = pickFromRecord(root, 'pacs_port', 'pacsPort');
  const pacsPort = pacsPortRaw || valueToDisplayString(facility.pacs_port) || '11112';

  const integrationStatus = pickFacilityStringOrDefault(
    root,
    facility.integration_status,
    ['integration_status', 'integrationStatus'],
    'Pending'
  );
  const statusRaw =
    pickFromRecord(root, 'status') || valueToDisplayString(facility.status) || 'Active';

  const employeeId = firstNonEmpty(
    adminUser?.employee_id,
    fromUser('employee_id'),
    pick('employee_id')
  );
  const fullName = firstNonEmpty(
    adminUser?.full_name,
    fromUser('full_name'),
    fromUser('fullName'),
    pick('full_name'),
    pick('fullName')
  );
  const adminPhoneNo = firstNonEmpty(
    adminUser?.mobile_number,
    fromUser('mobile_number'),
    fromUser('phone'),
    pick('admin_phone_no'),
    pick('adminPhoneNo')
  );
  const adminEmailId = firstNonEmpty(
    adminUser?.email,
    fromUser('email'),
    fromUser('email_id'),
    pick('admin_email_id'),
    pick('adminEmailId')
  );
  const genderRaw = firstNonEmpty(adminUser?.gender, fromUser('gender'), pick('gender'));
  const username = firstNonEmpty(adminUser?.username, fromUser('username'), pick('username'));
  const designation = firstNonEmpty(
    adminUser?.designation,
    fromUser('designation'),
    pick('designation')
  );

  const hasH =
    root.has_radiologist_hierarchy ??
    root.hasRadiologistHierarchy ??
    facility.has_radiologist_hierarchy;

  const seniorRaw = pickFromRecord(
    root,
    'senior_radiologist_review_required',
    'seniorRadiologistReviewRequired'
  );
  const seniorNorm =
    seniorRaw && ['Senior Radiologist', 'QC Technician'].includes(String(seniorRaw).trim())
      ? String(seniorRaw).trim()
      : 'Senior Radiologist';

  const hasAdminProfile = Boolean(adminUser || userRec || username || adminEmailId);
  const risUrl = pickFromRecord(root, 'ris_url', 'risUrl');
  const linkedHubRaw =
    root.linked_hub_facility_ids ??
    root.linkedHubFacilityIds ??
    (facility as Facility).linked_hub_facility_ids;
  const linkedHubFacilityIds: string[] = Array.isArray(linkedHubRaw)
    ? linkedHubRaw.map(id => String(id)).filter(Boolean)
    : [];

  return {
    name,
    code,
    type: normalizeFacilityTypeForForm(typeRaw),
    description,
    phoneNo: phoneNo.replaceAll(/\D/g, '').slice(-10),
    emailId,
    addressLine1,
    addressLine2,
    city,
    state,
    pincode,
    country,
    logoFileName: extractFilenameFromPath(letterheadPath || undefined),
    letterheadLogoUrl: letterheadPath || undefined,
    headerText,
    footerText,
    pacsAETitle,
    pacsIPAddress,
    pacsPort,
    integrationStatus,
    status: normalizeStatusForForm(statusRaw),
    employeeId,
    fullName,
    adminPhoneNo: adminPhoneNo.replaceAll(/\D/g, '').slice(-10),
    adminEmailId,
    gender: normalizeGenderForForm(genderRaw),
    username,
    password: hasAdminProfile ? '••••••' : '',
    designation: designation || '',
    hasRadiologistHierarchy: hasH === true || hasH === 'true' || hasH === 1 ? 'Yes' : 'No',
    seniorRadiologistReviewRequired: seniorNorm,
    ...(risUrl ? { risUrl } : {}),
    ...(linkedHubFacilityIds.length ? { linkedHubFacilityIds } : {}),
  };
}

/**
 * Map API response to FacilityManagement table format
 */
export function mapApiResponseToTableFormat(facility: Facility) {
  const addressParts = [
    facility.address_line_1,
    facility.address_line_2,
    facility.city,
    facility.state,
  ].filter(Boolean);
  const address = addressParts.length > 0 ? addressParts.join(', ') : '--';

  return {
    id: facility.id?.toString() || '--',
    facilityId: facility.facility_code || '--',
    name: facility.facility_name || '--',
    emailId: facility.email_id || '--',
    address: address,
    status: facility.status || 'Active',
    // Add other fields as needed for the table
  };
}
